import { Component, OnDestroy, OnInit, Signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Store } from '@ngrx/store';
import { ReactiveFormsModule } from '@angular/forms';
import { LocationDataForm } from './forms/location-data.form';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
import { NgxMaskDirective } from 'ngx-mask';
import {
  LocationDataParcelsSectionTitlePipe
} from './pipes/location-data-parcels-section-title.pipe';
import {
  LocationDataParcels2SectionTitlePipe
} from './pipes/location-data-parcels2-section-title.pipe';
import { selectApplication, selectLoading, selectPending } from '../../../../../store/selectors/application.selectors';
import { ApplicationAppEntityApplicationRead } from '../../../../../openapi';
import { SpinnerComponent } from '../../../../../common/spinner/spinner.component';
import { ValidationErrorsComponent } from '../../../../../common/validation-errors/validation-errors.component';
import { StagesComponent } from '../../../../../common/stages/stages.component';
import { ParcelsCrudComponent } from '../components/parcels/parcels-crud/parcels-crud.component';
import {
  ExposedMapControlComponent
} from '../../../../../common/exposed-map/exposed-map-control/exposed-map-control.component';
import { RoadControlsSetComponent } from '../components/road-controls-set/road-controls-set.component';
import { updateParcels } from '../../../../../store/actions/application.actions';
import { PropertyInfoFormComponent } from './components/property-info-form/property-info-form.component';

export interface LocationDataComponentConfig {
  stages: string[];
  successUrl: string;
  cancelUrl: string;
  addPropertyInfo?: boolean;
}

@Component({
  selector: 'app-location-data-section',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SpinnerComponent,
    ValidationErrorsComponent,
    StagesComponent,
    ParcelsCrudComponent,
    NgxMaskDirective,
    LocationDataParcelsSectionTitlePipe,
    LocationDataParcels2SectionTitlePipe,
    ExposedMapControlComponent,
    RoadControlsSetComponent,
    PropertyInfoFormComponent
  ],
  providers: [LocationDataForm],
  templateUrl: './location-data-section.component.html'
})
export class LocationDataSectionComponent implements OnInit, OnDestroy {

  public stages: string[] = this.route.snapshot.data['stages'];

  public applicationId: string | undefined = this.route.snapshot.params['appId'];

  public successUrl: string | undefined = this.route.snapshot.data['successUrl'];

  public cancelUrl: string | undefined = this.route.snapshot.data['cancelUrl'];

  public pending: Signal<boolean> = this.store$.selectSignal(selectPending);

  public loading: Signal<boolean> = this.store$.selectSignal(selectLoading);

  public application: Signal<ApplicationAppEntityApplicationRead | null> = this.store$.selectSignal(selectApplication);

  private fetchApplication: Subscription = toObservable(this.application).subscribe(app => {
    if (app) {
      this.form.patchFormValues(app);
    }
  });

  constructor(
    private store$: Store,
    private router: Router,
    private route: ActivatedRoute,
    public form: LocationDataForm
  ) {
  }

  public submitAndGoNext(): void {
    const currentApp = this.application();
    if (this.form.valid && currentApp) {
      this.store$.dispatch(updateParcels({
        currentApp,
        payload: this.form.toWriteModel(),
        parcels: this.form.parcelsValues,
        successFnc: (applicationId: string) => {
          this.router.navigate(
            [this.successUrl],
            {
              relativeTo: this.route
            }
          );
        },
        failureFnc: () => {
        }
      }));
    } else {
      this.form.markAllAsTouched();
    }
  }

  public onCancel(): void {
    this.router.navigate([this.cancelUrl], { relativeTo: this.route });
  }

  public ngOnInit(): void {
    if (this.route.snapshot.data['addPropertyInfo']) {
      this.form.createPropertyInfoForm();
    }
  }

  public ngOnDestroy(): void {
    this.fetchApplication.unsubscribe();
  }
}
